import { useQuery } from 'urql';
import {
  Grid,
  GridColumn,
  GridFilterChangeEvent,
  GridNoRecords,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { graphql } from '../../gql';
import React, { useState } from 'react';
import {
  filterBy,
  orderBy,
  CompositeFilterDescriptor,
  SortDescriptor,
} from '@progress/kendo-data-query';
import { Link } from '@tanstack/react-router';
import { PageTitle } from '../PageTitle.tsx';
import { HeroIcon } from '../../shared/HeroIcon.tsx';

interface SeminarsPageProps {
  companyId: string;
}

const SeminarsQuery = graphql(/* GraphQL */ `
  query getSeminars($companyId: String!) {
    seminars(companyId: $companyId) {
      id
      seminarNumber
      title
      subtitle
      startDate
      endDate
      registrationDate
      description
      visible
      lecturers {
        name
      }
    }
  }
`);

export const SeminarsPage: React.FC<SeminarsPageProps> = ({ companyId }) => {
  // TODO: Error handling
  const [{ data, fetching }] = useQuery({
    query: SeminarsQuery,
    variables: { companyId },
    pause: companyId === undefined || companyId === null,
  });

  const toStartOfDay = (value: string | Date) => {
    const d = new Date(value);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const parsedData =
    data?.seminars.map((seminar) => ({
      ...seminar,
      startDate: toStartOfDay(seminar.startDate),
      endDate: toStartOfDay(seminar.endDate),
      lecturers: seminar.lecturers
        .map((lecturer) => lecturer.name)
        .filter(Boolean)
        .join(', '),
      registrationDate:
        seminar.registrationDate && toStartOfDay(seminar.registrationDate),
    })) ?? [];

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({
    logic: 'and',
    filters: [],
  });
  const [sort, setSort] = useState<SortDescriptor[]>([]);
  const [page, setPage] = useState({
    skip: 0,
    take: 10,
  });

  const filteredData = orderBy(filterBy(parsedData, filter), sort);

  return (
    <>
      <PageTitle title="Seminare" />
      <Grid
        className="h-[653px] [&_.k-grid-norecords-template]:border-0 [&_.k-table-td]:whitespace-nowrap"
        showLoader={fetching}
        data={filteredData.slice(page.skip, page.take + page.skip)}
        filterable={true}
        filter={filter}
        onFilterChange={(e: GridFilterChangeEvent) => setFilter(e.filter)}
        sortable
        sort={sort}
        onSortChange={(e: GridSortChangeEvent) => {
          setSort(e.sort);
          setPage({ ...page, skip: 0 });
        }}
        pageable
        take={page?.take}
        skip={page?.skip}
        total={filteredData.length}
        onPageChange={(event) => {
          setPage(event.page);
        }}
      >
        <GridNoRecords>
          {fetching ? '' : 'Keine Seminare gefunden'}
        </GridNoRecords>
        <GridColumn field="seminarNumber" title="Seminar Nr." />
        <GridColumn field="title" title="Titel" />
        {/* <GridColumn field="subtitle" title="Untertitel" /> */}
        <GridColumn field="lecturers" title="Dozent" />
        <GridColumn
          field="startDate"
          title="Startdatum"
          filter="date"
          format="{0:dd.MM.yyyy}"
        />
        <GridColumn
          field="endDate"
          title="Enddatum"
          filter="date"
          format="{0:dd.MM.yyyy}"
        />
        <GridColumn
          field="registrationDate"
          title="Anmeldeschluss"
          filter="date"
          format="{0:dd.MM.yyyy}"
        />
        <GridColumn
          width={'120px'}
          filterable={false}
          sortable={false}
          cells={{
            data: (props) => (
              <td className={'k-button-link-secondary flex space-x-2'}>
                <Link
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  to={`/admin/seminars/edit/${props.dataItem.id}` as any}
                  className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md text-red"
                >
                  <HeroIcon
                    name="Pencil"
                    className="block h-5 w-5"
                    aria-label="Bearbeiten"
                  />
                </Link>
                {props.dataItem.visible && (
                  <a
                    target="_blank"
                    href={`/seminar/${props.dataItem.id}`}
                    className="k-button k-button-md k-button-outline k-button-outline-secondary k-rounded-md text-red"
                  >
                    <HeroIcon
                      name="GlobeAlt"
                      className="block h-5 w-5"
                      aria-label="Öffentliche Seite öffnen"
                    />
                  </a>
                )}
              </td>
            ),
          }}
        />
      </Grid>
    </>
  );
};
